// Drawer.tsx
"use client";

import React, { useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import styles from "./drawer.module.css";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  closeDrawer,
  openDrawer,
  toggleDrawer,
} from "../../../store/features/drawer/drawer.slice";
import Button from "../../common/Button";

interface Props {
  drawerItems?: DrawerItem[];
  profileSection?: boolean;
  headerSection?: boolean;
  isAuthenticated: boolean;
}

interface DrawerItem {
  name: string;
  href: string;
  icon: React.ReactNode;
}

const defaultDrawerItems: DrawerItem[] = [
  {
    name: "Home",
    href: "/",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    name: "Search",
    href: "/search",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
    ),
  },
  {
    name: "Create",
    href: "/create",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="16" />
        <line x1="8" y1="12" x2="16" y2="12" />
      </svg>
    ),
  },
  {
    name: "Activity",
    href: "/activity",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    ),
  },
  {
    name: "Profile",
    href: "/profile",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
  },
  {
    name: "Settings",
    href: "/settings",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="12" cy="12" r="3" />
        <path d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24" />
      </svg>
    ),
  },
  {
    name: "Help",
    href: "/help",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    ),
  },
];

export function useDrawer() {
  const dispatch = useAppDispatch();
  const { isOpen } = useAppSelector((state) => state.drawer);

  return {
    isOpen,
    openDrawer: function () {
      dispatch(openDrawer());
    },
    closeDrawer: function () {
      dispatch(closeDrawer());
    },
    toggleDrawer: function () {
      dispatch(toggleDrawer());
    },
  };
}

const Drawer: React.FC<Props> = ({
  drawerItems = defaultDrawerItems,
  profileSection = true,
  headerSection = true,
  isAuthenticated,
}) => {
  const pathname = usePathname();
  const { isOpen, closeDrawer } = useDrawer();

  // Close drawer on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDrawer();
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, closeDrawer]);

  return (
    <>
      {/* Overlay */}
      <div
        className={`${styles.overlay} ${isOpen ? styles.overlayActive : ""}`}
        onClick={closeDrawer}
      />

      {/* Drawer */}
      <div className={`${styles.drawer} ${isOpen ? styles.drawerOpen : ""}`}>
        {/* Header */}
        {headerSection && (
          <div className={styles.drawerHeader}>
            <h2 className={styles.drawerTitle}>Menu</h2>
            <button
              className={styles.closeButton}
              onClick={closeDrawer}
              aria-label="Close drawer"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        )}

        {/* User Info Section */}
        {profileSection && isAuthenticated ? (
          <div className={styles.userSection}>
            <div className={styles.userAvatar}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <div className={styles.userInfo}>
              <h3 className={styles.userName}>John Doe</h3>
              <p className={styles.userEmail}>john.doe@example.com</p>
            </div>
          </div>
        ) : (
          <div
            className={styles.userSection}
            style={{ justifyContent: "center" }}
          >
            <Button href="/login" title="Login" variant="secondary" />
            <Button href="/register" title="Sign up" appearance="outline" />
          </div>
        )}

        {/* Navigation Items */}
        <nav className={styles.drawerNav}>
          {drawerItems.map((item) => {
            const isActive = pathname === item.href;

            return (
              <Link
                key={item.name}
                href={item.href}
                className={`${styles.drawerItem} ${
                  isActive ? styles.drawerItemActive : ""
                }`}
                onClick={closeDrawer}
              >
                <div className={styles.drawerIcon}>{item.icon}</div>
                <span className={styles.drawerLabel}>{item.name}</span>
                {isActive && <div className={styles.activeIndicator} />}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className={styles.drawerFooter}>
          {isAuthenticated ? (
            <button className={styles.logoutButton}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span>Logout</span>
            </button>
          ) : (
            <Link className={styles.logoutButton} href={"/login"}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span>Login</span>
            </Link>
          )}
        </div>
      </div>
    </>
  );
};

export default Drawer;
